/**
 * TEMPORARY: scan tuning probe, to be removed once the thresholds in
 * `scanDetection.const.ts` have been tuned. Removal is this file, its spec, the
 * `finalizeAndLogTuning` wrapper in `scanDetector.util.ts` and the
 * `QUICKPACK_SCAN_TUNING_LOGS` flag.
 */
import { FeatureFlags, decideFeatureFlag } from '~/utils/featureflags';
import { newRelic } from '~/utils/newRelic/newRelic';

import { type ScanDetectorConfig } from '../types/scanDetector.type';

/** Timestamps only, so the probe cannot read what was typed even by accident. */
type ScanTuningCandidate = readonly { timestamp: number }[];

type ScanTuningRejectionReason =
  | 'inter_key_slack_exceeded'
  | 'max_avg_time_exceeded'
  | 'max_duration_exceeded';

type ScanTuningTrigger = 'suffix' | 'timeout';

const MIN_LOGGED_CHARACTER_COUNT = 2;
const MAX_LOGGED_MEAN_INTERVAL_MULTIPLIER = 4;

export const createScanTuningProbe = (settings: ScanDetectorConfig) => {
  let previousCandidateEndTimestamp: number | undefined;

  return (candidate: ScanTuningCandidate, finalizedBy: ScanTuningTrigger): void => {
    if (candidate.length < MIN_LOGGED_CHARACTER_COUNT) return;

    if (!decideFeatureFlag(FeatureFlags.QUICKPACK_SCAN_TUNING_LOGS, { blockEvent: true })?.enabled)
      return;

    const firstTimestamp = candidate[0].timestamp;
    const lastTimestamp = candidate[candidate.length - 1].timestamp;
    const intervals = candidate
      .slice(1)
      .map((character, index) => character.timestamp - candidate[index].timestamp);
    const meanIntervalMs =
      intervals.reduce((total, interval) => total + interval, 0) / intervals.length;

    if (meanIntervalMs > settings.maxAvgTimeMs * MAX_LOGGED_MEAN_INTERVAL_MULTIPLIER) return;

    // Advance only when a record is emitted, so the gap points at the row above this one
    // in the log rather than at typing the filter dropped.
    const msSincePreviousCandidate =
      previousCandidateEndTimestamp === undefined
        ? undefined
        : firstTimestamp - previousCandidateEndTimestamp;
    previousCandidateEndTimestamp = lastTimestamp;

    const durationMs = lastTimestamp - firstTimestamp;
    const slowGapCount = intervals.filter((interval) => interval > settings.maxInterKeyMs).length;
    const isTooShort = candidate.length < settings.minLength;
    const rejectedBy: ScanTuningRejectionReason[] = [];

    // `finalize` returns before the timing checks for a short candidate, so leave the
    // reasons empty rather than reporting limits it never evaluated.
    if (!isTooShort) {
      if (meanIntervalMs > settings.maxAvgTimeMs) rejectedBy.push('max_avg_time_exceeded');
      if (durationMs > settings.maxDurationMs) rejectedBy.push('max_duration_exceeded');
      if (slowGapCount > settings.interKeySlackCount) rejectedBy.push('inter_key_slack_exceeded');
    }

    // A duplicate dropped inside `duplicateWindowMs` counts as accepted here: it cleared
    // every limit these six parameters govern.
    const outcome = isTooShort
      ? 'rejected_min_length'
      : rejectedBy.length > 0
      ? 'rejected_timing'
      : 'accepted';

    newRelic.log('Scan tuning candidate', {
      customAttributes: {
        characterCount: candidate.length,
        configBufferTimeoutMs: settings.bufferTimeoutMs,
        configInterKeySlackCount: settings.interKeySlackCount,
        configMaxAvgTimeMs: settings.maxAvgTimeMs,
        configMaxDurationMs: settings.maxDurationMs,
        configMaxInterKeyMs: settings.maxInterKeyMs,
        configMinLength: settings.minLength,
        durationMs,
        finalizedBy,
        maxIntervalMs: Math.max(...intervals),
        meanIntervalMs,
        msSincePreviousCandidate,
        outcome,
        rejectedBy: rejectedBy.join(','),
        slowGapCount,
      },
      level: 'INFO',
    });
  };
};
